import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Share2, Trash2, Lock, Globe, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, API_BASE } from "@/lib/api";
import { fmtTime } from "@/lib/hubs";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/store/player";
import { TrackRow } from "@/components/TrackItems";
import { ArtistBadge } from "@/components/ArtistBadge";
import { CollectionCover } from "@/components/Collections";
import { Switch } from "@/components/ui/switch";

export default function CollectionPage({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const load = () => api.get(`/${kind}s/${id}`).then((r) => { setD(r.data); setTitle(r.data.title); document.title = `${r.data.title} | Soundra`; }).catch(() => setD(false));
  useEffect(() => { load(); }, [id, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  if (d === false) return <div className="py-24 text-center text-zinc-400" data-testid="collection-not-found">This {kind} is unavailable.</div>;
  if (!d) return <div className="h-80 rounded-3xl bg-white/5 animate-pulse" />;
  const isOwner = user && d.owner?.user_id === user.user_id;
  const patch = async (body) => { try { await api.patch(`/${kind}s/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    await api.delete(`/${kind}s/${id}`);
    toast.success("Deleted");
    nav("/dashboard?tab=" + (kind === "album" ? "albums" : "playlists"));
  };
  const remove = async (tid) => { await api.delete(`/playlists/${id}/tracks/${tid}`); load(); };
  const share = () => { navigator.clipboard?.writeText(`${API_BASE}/api/share/collection/${kind}/${id}`); toast.success("Share link copied"); };
  const ownerLink = d.owner?.role === "listener" ? null : `/artist/${d.owner?.slug}`;
  return (
    <div data-testid={`${kind}-page`} className="reveal">
      <div className="grid md:grid-cols-[300px_1fr] gap-8 items-end">
        <CollectionCover url={d.cover_url} covers={d.covers} className="shadow-2xl" />
        <div className="min-w-0">
          <div className="eyebrow flex items-center gap-2">{kind === "album" ? d.type : "Playlist"} {kind === "playlist" && (d.is_public ? <Globe size={12} /> : <Lock size={12} />)}</div>
          {editing ? (
            <div className="flex gap-2 mt-2">
              <input data-testid="collection-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="flex-1 h-14 px-4 rounded-xl bg-white/5 border border-white/10 font-display text-3xl font-black outline-none" />
              <button data-testid="collection-title-save" onClick={() => { patch({ title }); setEditing(false); }} className="px-5 rounded-xl bg-white text-black font-semibold">Save</button>
            </div>
          ) : (
            <h1 data-testid="collection-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-2 break-words">{d.title}</h1>
          )}
          {d.description && <p className="text-zinc-400 mt-3 max-w-2xl">{d.description}</p>}
          <div className="flex items-center gap-2 mt-4 text-sm text-zinc-300">
            {d.owner?.avatar && <img src={mediaUrl(d.owner.avatar)} alt="" className="w-7 h-7 rounded-full object-cover" />}
            {ownerLink ? <Link to={ownerLink} className="font-semibold hover:underline">{d.owner?.name}</Link> : <span className="font-semibold">{d.owner?.name}</span>}
            <ArtistBadge badge={d.owner?.badge} />
            <span className="text-zinc-500 font-mono text-xs">· {d.tracks.length} tracks · {fmtTime(d.duration)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button data-testid="collection-play-all" disabled={!d.tracks.length} onClick={() => usePlayer.getState().playTrack(d.tracks[0], d.tracks)} className="h-12 px-7 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40">Play all</button>
            <button data-testid="collection-share-button" onClick={share} className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center hover:border-white/40" aria-label="Share"><Share2 size={16} /></button>
            {isOwner && (<>
              <button data-testid="collection-edit-button" onClick={() => setEditing(!editing)} className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center hover:border-white/40" aria-label="Rename"><Pencil size={16} /></button>
              {kind === "playlist" && <label className="flex items-center gap-2 text-sm text-zinc-400 ml-2">Public <Switch data-testid="playlist-public-switch" checked={d.is_public} onCheckedChange={(v) => patch({ is_public: v })} /></label>}
              <button data-testid="collection-delete-button" onClick={del} className="w-12 h-12 rounded-full border border-red-500/30 text-red-300 flex items-center justify-center hover:bg-red-500/10" aria-label="Delete"><Trash2 size={16} /></button>
            </>)}
          </div>
        </div>
      </div>
      <div className="surface p-2 mt-10" data-testid="collection-tracklist">
        {d.tracks.length === 0 && <div className="p-8 text-zinc-500">No tracks yet. Use “Add to playlist” on any track or in the player.</div>}
        {d.tracks.map((t, i) => (
          <div key={t.track_id} className="flex items-center">
            <div className="flex-1 min-w-0"><TrackRow track={t} list={d.tracks} rank={i + 1} /></div>
            {isOwner && kind === "playlist" && <button data-testid={`playlist-remove-${t.track_id}`} onClick={() => remove(t.track_id)} className="px-3 text-zinc-500 hover:text-red-400" aria-label="Remove"><X size={16} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
